import {
  type UseMutateAsyncFunction,
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysUser } from './queryKeys';
import { type UserData } from './useQueryUserData';

type UserSettings = Record<string, unknown>;

type MutationUserArguments = {
  userId: UUID;
  userSettings: Partial<UserSettings>;
};

type UpdateUserSettingsResponse = {
  code: number;
  id: UUID;
  requestId: string;
};

type UpdateUserSettingsMutationOptions = UseMutationOptions<
  UpdateUserSettingsResponse,
  Error,
  MutationUserArguments
>;

export type UpdateUserSettingsMutateAsyncFunction = UseMutateAsyncFunction<
  UpdateUserSettingsResponse,
  Error,
  MutationUserArguments
>;

/**
 * Updates the settings of a given user.
 *
 * @param {string} userId - The ID of the user to update.
 * @param {Object} featureFlags - An object containing the settings to update.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Admin/operation/update_user_settings_admin_user__id__settings_post
 */
export const updateUserSettings = async (
  userId: UUID,
  userSettings: Partial<UserSettings>,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.USER.UPDATE_SETTINGS(userId), {
      json: userSettings,
    })
    .json<UpdateUserSettingsResponse>();

  return response;
};

/**
 * Custom hook for updating the settings of a given user.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationUpdateUserSettings = (
  options?: UpdateUserSettingsMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, userSettings }: MutationUserArguments) =>
      updateUserSettings(userId, userSettings),
    onSuccess(_, { userId, userSettings }) {
      // Patch the current user's cached `/user/data` settings and only mark
      // the query stale: an immediate refetch can race the write and serve the
      // pre-update settings again (e.g. the user type on /account staying
      // stale until reload). The id guard keeps admin edits of other users
      // out of the own-user cache.
      queryClient.setQueryData<UserData>(
        queryKeysUser.getData(true),
        (cachedUserData) =>
          cachedUserData &&
          (cachedUserData.settings as { id?: UUID }).id === userId
            ? {
                ...cachedUserData,
                settings: { ...cachedUserData.settings, ...userSettings },
              }
            : cachedUserData,
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeysUser.get(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysUser.getData(true),
        refetchType: 'none',
      });
    },
    ...options,
  });
};
